import { useCallback, useMemo } from "react";
import {
  ActionIcon,
  Group,
  Indicator,
  Loader,
  Menu,
  Stack,
  Text,
} from "@mantine/core";
import { IconBell, IconUserCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { QcFailureNoticeItems } from "./QcFailureNoticeItems";
import { useQcFailureNotices } from "../../hooks/useQcFailureNotices";
import { usePathologistAssignmentNotices } from "../../hooks/usePathologistAssignmentNotices";
import type { AssignmentNoticeRecipientRole } from "../../types/casePathologistAssignment";

interface PathologistAssignmentNoticeMenuProps {
  enabled: boolean;
}

const recipientLabel: Record<AssignmentNoticeRecipientRole, string> = {
  incoming: "Assigned to you",
  outgoing: "Transferred from you",
  active_reviewer: "Review assignment changed",
};

export default function PathologistAssignmentNoticeMenu({
  enabled,
}: PathologistAssignmentNoticeMenuProps) {
  const navigate = useNavigate();
  const qcNotices = useQcFailureNotices(enabled);
  const {
    notices,
    unreadCount: assignmentUnreadCount,
    isLoading,
    markRead,
  } = usePathologistAssignmentNotices({ enabled });

  const unreadCount = useMemo(
    () => assignmentUnreadCount + qcNotices.unreadCount,
    [assignmentUnreadCount, qcNotices.unreadCount]
  );
  const handleNoticeSelect = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const noticeId = event.currentTarget.dataset.noticeId;
      const caseId = event.currentTarget.dataset.caseId;
      const isUnread = event.currentTarget.dataset.unread === "true";
      if (!noticeId || !caseId) return;
      if (isUnread) void markRead(noticeId);
      navigate(`/cases/${caseId}`);
    },
    [markRead, navigate]
  );

  return (
    <Menu
      shadow="md"
      width="min(22.5rem, calc(100vw - var(--mantine-spacing-md) * 2))"
      position="bottom-end"
      offset={8}
      withinPortal
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={`Assignment notices, ${unreadCount} unread`}
        >
          <Indicator
            inline
            size={16}
            label={unreadCount > 9 ? "9+" : unreadCount}
            disabled={unreadCount === 0}
            aria-hidden="true"
          >
            <IconBell size={20} />
          </Indicator>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown
        aria-label="Pathologist assignment notices"
        style={{
          maxHeight: "calc(100dvh - var(--mantine-spacing-xl) * 2)",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <Menu.Label>Pathologist assignment notices</Menu.Label>
        {isLoading ? (
          <Group justify="center" p="md">
            <Loader size="sm" aria-label="Loading assignment notices" />
          </Group>
        ) : notices.length === 0 ? (
          <Text size="sm" c="dimmed" p="md">
            No assignment notices
          </Text>
        ) : (
          notices.map((notice) => (
            <Menu.Item
              key={notice.id}
              leftSection={<IconUserCheck size={18} />}
              data-notice-id={notice.id}
              data-case-id={notice.caseId}
              data-unread={!notice.readAt}
              onClick={handleNoticeSelect}
            >
              <Stack gap={3}>
                <Group justify="space-between" gap="xs" wrap="wrap">
                  <Text size="sm" fw={notice.readAt ? 500 : 700} flex={1}>
                    {recipientLabel[notice.recipientRole]} — {notice.caseNumber}
                  </Text>
                  <Text size="xs" fw={notice.readAt ? 500 : 700}>
                    {notice.readAt ? "Read" : "Unread"}
                  </Text>
                </Group>
                <Text size="xs">{notice.message}</Text>
                <Text size="xs">Reason: {notice.reason}</Text>
                {notice.recordedWorkPreserved && (
                  <Text size="xs">
                    Recorded work saved before reassignment remains available.
                  </Text>
                )}
                {!notice.unsavedDeviceWorkPreserved && (
                  <Text size="xs">
                    Unsaved device-only changes were not transferred.
                  </Text>
                )}
                {notice.followUps.map((followUp) => (
                  <Text
                    key={`${followUp.code}-${followUp.message}`}
                    size="xs"
                    fw={600}
                  >
                    Follow-up: {followUp.message}
                    {followUp.followUp ? ` ${followUp.followUp}` : ""}
                  </Text>
                ))}
                <Text size="xs" c="dimmed">
                  Current pathologist:{" "}
                  {notice.currentAssignment.pathologist.fullName}
                </Text>
                {notice.ownershipChangedAgain && (
                  <Text size="xs" fw={600}>
                    Ownership changed again after this notice.
                  </Text>
                )}
              </Stack>
            </Menu.Item>
          ))
        )}
        <Menu.Divider />
        <QcFailureNoticeItems
          notices={qcNotices.notices}
          markRead={qcNotices.markRead}
        />
      </Menu.Dropdown>
    </Menu>
  );
}
